import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ChevronLeft, MapPin, Globe, Calendar, Clock } from "lucide-react";

const fmtUsd = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

export default function LessonsCoachDetail() {
  const { coachId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const [slot, setSlot] = useState("");  // local datetime input
  const [studentName, setStudentName] = useState(user?.name || "");
  const [studentEmail, setStudentEmail] = useState(user?.email || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/lessons/coaches/${coachId}`).then(({ data }) => {
      setData(data);
      const first = data.offering.enabled_lesson_types?.[0];
      if (first) setPicked(first);
    }).catch(() => toast.error("Coach listing not found"));
  }, [coachId]);

  const book = async () => {
    if (!user) { nav(`/login?redirect=/lessons/coach/${coachId}`); return; }
    if (!picked || !slot) { toast.error("Pick a lesson type and date/time"); return; }
    setSubmitting(true);
    try {
      const slotIso = new Date(slot).toISOString();
      const { data: res } = await api.post("/lessons/bookings/checkout", {
        coach_user_id: coachId,
        lesson_type_id: picked,
        slot_iso: slotIso,
        student_name: studentName,
        student_email: studentEmail,
        student_notes: notes || null,
        origin_url: window.location.origin,
      });
      if (res.url) { window.location.href = res.url; }
      else { toast.error("Checkout session missing URL"); }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm" style={{ backgroundColor: "var(--ink-1)" }}>Loading…</div>;

  const o = data.offering;
  const catalog = data.catalog;
  const pickedPkg = picked ? catalog[picked] : null;
  const payoutCents = pickedPkg ? Math.round(pickedPkg.price_cents * (100 - data.platform_fee_pct) / 100) : 0;
  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white";

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }}>
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <Link to="/lessons" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="back-to-marketplace">
            <ChevronLeft className="h-4 w-4" /> Back to marketplace
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 mt-8 grid md:grid-cols-[1fr_360px] gap-6">
        {/* Coach profile */}
        <div className="space-y-5">
          <div className="rounded-2xl ink-card p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-cyan-400/15 flex items-center justify-center text-cyan-300 font-bold text-2xl">
                {o.headshot_url ? <img src={o.headshot_url} alt={o.coach_name} className="w-16 h-16 rounded-full object-cover" /> : (o.coach_name || "C").slice(0, 1)}
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>ArmIQ Verified Coach</div>
                <h1 className="font-display font-black text-3xl text-white tracking-tight">{o.coach_name}</h1>
                <div className="flex flex-wrap gap-3 text-xs text-white/60 mt-1">
                  {o.location_city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location_city}</span>}
                  {o.timezone && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> {o.timezone}</span>}
                </div>
              </div>
            </div>
            {o.bio && <p className="text-sm text-white/75 mt-5 leading-relaxed whitespace-pre-wrap">{o.bio}</p>}
            {(o.specialties || []).length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {o.specialties.map((s) => (
                  <span key={s} className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-cyan-400/10 text-cyan-200 border border-cyan-400/30">{s}</span>
                ))}
              </div>
            )}
          </div>

          {/* Weekly availability */}
          {(o.availability || []).length > 0 && (
            <div className="rounded-2xl ink-card p-6">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Recurring availability</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {o.availability.map((w, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 bg-white/[0.03] border border-white/10 text-sm">
                    <span className="font-bold text-white">{DAY_LABEL[w.day]}</span>
                    <span className="text-white/70 font-mono-num">{w.start_hhmm} – {w.end_hhmm}</span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-white/40 mt-3">Pick any date/time within these windows when booking.</div>
            </div>
          )}
        </div>

        {/* Booking panel */}
        <aside className="rounded-2xl ink-card p-5 h-fit sticky top-24" data-testid="booking-panel">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Book a session</div>
          <h2 className="font-display font-bold text-xl text-white mt-1">Pick a lesson type</h2>

          <div className="mt-4 space-y-2">
            {(o.enabled_lesson_types || []).map((id) => {
              const c = catalog[id];
              const isOn = picked === id;
              return (
                <button key={id} type="button" onClick={() => setPicked(id)}
                  className={`w-full text-left rounded-xl p-3 border transition ${isOn ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                  data-testid={`booking-type-${id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-white text-sm">{c.label}</div>
                      <div className="text-[11px] text-white/50">{c.duration_min} min · {c.max_seats === 1 ? "1:1" : `up to ${c.max_seats} seats`}</div>
                    </div>
                    <div className="font-display font-black text-lg text-white">{fmtUsd(c.price_cents)}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {pickedPkg && (
            <>
              <div className="mt-5">
                <Label className="text-white/70 flex items-center gap-1"><Calendar className="h-3 w-3" /> Date &amp; time</Label>
                <Input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} className={`mt-1.5 ${inputCls}`} data-testid="booking-slot" />
              </div>
              <div className="mt-3">
                <Label className="text-white/70">Your name</Label>
                <Input value={studentName} onChange={(e) => setStudentName(e.target.value)} className={`mt-1.5 ${inputCls}`} data-testid="booking-name" />
              </div>
              <div className="mt-3">
                <Label className="text-white/70">Email</Label>
                <Input type="email" value={studentEmail} onChange={(e) => setStudentEmail(e.target.value)} className={`mt-1.5 ${inputCls}`} data-testid="booking-email" />
              </div>
              <div className="mt-3">
                <Label className="text-white/70">Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Goals, things to work on, etc." className={`mt-1.5 min-h-[70px] ${inputCls}`} data-testid="booking-notes" />
              </div>

              <div className="mt-4 rounded-lg bg-white/[0.03] border border-white/10 p-3 text-xs text-white/70 space-y-1">
                <div className="flex justify-between"><span>Lesson</span><span className="text-white font-bold">{fmtUsd(pickedPkg.price_cents)}</span></div>
                <div className="flex justify-between text-white/50"><span>Coach receives</span><span className="font-mono-num">{fmtUsd(payoutCents)}</span></div>
                <div className="flex justify-between text-white/50"><span>Platform fee ({data.platform_fee_pct}%)</span><span className="font-mono-num">{fmtUsd(pickedPkg.price_cents - payoutCents)}</span></div>
              </div>

              <Button onClick={book} disabled={submitting || !slot || !picked || !studentName || !studentEmail}
                className="w-full mt-4 h-11 text-white font-bold red-glow"
                style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="booking-checkout-btn">
                {submitting ? "Redirecting…" : "Book & Pay"}
              </Button>
              <div className="text-[10px] text-white/40 text-center mt-2">Payment processed securely · cancel up to 24h before</div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
